import { type ReactNode, useEffect, useState } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";

import { Loading } from "design-system/components/Loading/Loading";
import { useLoginFlows } from "shell/platform/contexts/LoginFlowContext";
import { useDecodedTokens } from "shell/platform/hooks/useDecodedTokens";
import { useIdleLogout } from "shell/platform/hooks/useIdleLogout";
import { useRoles } from "shell/platform/hooks/useRoles";
import { loadAmplitude } from "shell/platform/lib/analytics/amplitudeLoader";
import { rumInstance } from "shell/platform/lib/analytics/rum";
import { refreshCognitoTokens } from "shell/platform/lib/cognitoPasswordAuth";
import {
  clearLoginHint,
  getLoginHint,
  getStoredAuthProvider,
  getUserProfileInfo,
  isCognito,
  isCognitoPasswordMode,
} from "shell/platform/lib/oidcClient";

interface CheckUserSessionProps {
  children: ReactNode;
}

export const CheckUserSession = ({ children }: CheckUserSessionProps) => {
  const { PasswordLogin, IdpDiscovery } = useLoginFlows();
  const {
    user,
    isLoading,
    activeNavigator,
    isAuthenticated,
    signinSilent,
    signinRedirect,
    events,
  } = useAuth();
  const [needsIdpDiscovery, setNeedsIdpDiscovery] = useState(false);
  const [needsPasswordReauth, setNeedsPasswordReauth] = useState(false);

  const { accessToken } = useDecodedTokens();
  const { hasInternalAccess } = useRoles();
  const userId = accessToken?.sub;

  useIdleLogout();

  useEffect(() => {
    if (!events) return;

    const handlePasswordRefresh = () => {
      const refreshToken = user?.refresh_token;
      if (!refreshToken) {
        setNeedsPasswordReauth(true);
        return;
      }

      refreshCognitoTokens(refreshToken).then(refreshed => {
        if (!refreshed) setNeedsPasswordReauth(true);
      });
    };

    const handleAccessTokenExpiring = () => {
      if (!isCognitoPasswordMode()) return;
      handlePasswordRefresh();
    };

    const handleSigninRedirect = () => {
      if (isCognitoPasswordMode()) {
        setNeedsPasswordReauth(true);
        return;
      }
      signinRedirect();
    };

    events.addAccessTokenExpiring(handleAccessTokenExpiring);
    events.addSilentRenewError(handleSigninRedirect);
    events.addAccessTokenExpired(handleSigninRedirect);

    return () => {
      events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      events.removeSilentRenewError(handleSigninRedirect);
      events.removeAccessTokenExpired(handleSigninRedirect);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [events, signinSilent, user?.refresh_token]);

  useEffect(() => {
    if (
      hasAuthParams() ||
      isLoading ||
      activeNavigator ||
      isAuthenticated ||
      needsIdpDiscovery ||
      needsPasswordReauth
    ) {
      return;
    }
    (async () => {
      if (isCognitoPasswordMode()) {
        setNeedsPasswordReauth(true);
        return;
      }
      const user = await signinSilent();
      if (user === null) {
        if (isCognito() || !getStoredAuthProvider()) {
          setNeedsIdpDiscovery(true);
        } else {
          const loginHint = getLoginHint();
          clearLoginHint();
          await signinRedirect(
            loginHint
              ? { extraQueryParams: { login_hint: loginHint } }
              : undefined,
          );
        }
      }
    })();
  }, [
    isLoading,
    isAuthenticated,
    activeNavigator,
    signinSilent,
    signinRedirect,
    needsIdpDiscovery,
    needsPasswordReauth,
  ]);
  useEffect(() => {
    const userInfo = getUserProfileInfo(user);
    if (userInfo) {
      if (rumInstance) {
        rumInstance.addSessionAttributes({
          email: userInfo.email ?? "Not set",
          company_name: userInfo.organizationName ?? "Not set",
          name: userInfo.name,
        });
      }
    }
  }, [user]);

  // Identify in Amplitude without email or name. Company is only sent for
  // external users, and matches the RUM company_name above; no email or name is sent.
  useEffect(() => {
    if (!userId) return;

    void (async () => {
      const amplitude = await loadAmplitude();
      amplitude?.identifyUser({
        userId,
        userType: hasInternalAccess ? "internal" : "external",
        company: getUserProfileInfo(user)?.organizationName,
      });
    })();
  }, [user, userId, hasInternalAccess]);

  if (needsPasswordReauth) {
    const reload = () => globalThis.location.reload();

    return (
      <PasswordLogin
        initialEmail={user?.profile.email}
        onAuthenticated={reload}
        onCancel={reload}
      />
    );
  }

  if (needsIdpDiscovery) return <IdpDiscovery />;
  if (!user || isLoading) return <Loading isFullScreen size="large" />;
  return children;
};
